import { css } from '@emotion/react';
import { CLOUD_SIGNUP_FLOW_EVENT, PUBLIC_URL } from 'utils/consts';
import { useStyles } from 'utils/useStyles';
import FriendlyMessages from './FriendlyMessages';
import Starfield from './Starfield';
import { useContext, useEffect } from 'react';
import { TrackingContext } from 'context/Tracking';

const HEARTBEAT_DELAY = 5000;

const GrotRocketAnimationLoader = () => {
  const styles = useStyles(getStyles);
  const [, { trackEvent }] = useContext(TrackingContext);

  useEffect(() => {
    let heartbeatCounter = 1;
    trackEvent({
      name: CLOUD_SIGNUP_FLOW_EVENT,
      componentKey: `grot_rocket_loader_page_load`,
    });

    setInterval(() => {
      trackEvent({
        name: CLOUD_SIGNUP_FLOW_EVENT,
        componentKey: `grot_rocket_loader_heartbeat`,
        heartbeatCounter: (heartbeatCounter * HEARTBEAT_DELAY) / 1000,
      });
      heartbeatCounter = heartbeatCounter + 1;
    }, HEARTBEAT_DELAY);

    return () => {
      trackEvent({
        name: CLOUD_SIGNUP_FLOW_EVENT,
        componentKey: `grot_rocket_loader_page_unmount`,
      });
    };
  }, [trackEvent]);

  return (
    <div css={styles.page}>
      <Starfield />
      <div css={styles.content}>
        <main>
          <img css={styles.logo} src={`${PUBLIC_URL}/img/grafana-logo.svg`} alt="Grafana Logo" />
          <div id="grot-loader-welcome" css={styles.text}>
            Welcome, let's launch Grafana Cloud
          </div>
        </main>
        <div aria-hidden="true">
          <img css={styles.grot} src={`${PUBLIC_URL}/img/loaders/grot-rocket-loader/grot-spacesuit.svg`} />
          <div css={styles.rocketContainer}>
            <img css={styles.rocket} src={`${PUBLIC_URL}/img/loaders/grot-rocket-loader/rocket.svg`} />
            <div css={styles.rocketTrail}></div>
          </div>
          <div css={styles.fadeIn}>
            <div css={styles.bar}>
              <div css={styles.sweep}></div>
            </div>
            <FriendlyMessages />
          </div>
        </div>
      </div>
    </div>
  );
};

const getStyles = () => ({
  page: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  content: css`
    position: absolute;
    top: calc(50% - 200px);
    width: 550px;

    @media (max-width: 767px) {
      scale: 0.6;
    }
  `,
  logo: css`
    display: block;
    height: 72px;
    margin: 0px auto 24px;
  `,
  text: css`
    text-align: center;
    font-size: 24px;
    color: black;
    letter-spacing: -0.02em;
    margin-bottom: 32px;

    animation: blackToWhite 0.5s 2.4s linear forwards;

    @keyframes blackToWhite {
      from {
        color: #16181d;
      }

      to {
        color: #eceef1;
      }
    }

    @media (max-width: 767px) {
      font-size: 32px;
    }
  `,
  fadeIn: css`
    position: relative;
    top: 134px;
    opacity: 0;

    animation: fadeIn 2s 5s forwards;

    @keyframes fadeIn {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }
  `,
  bar: css`
    margin: 0px auto;
    width: 220px;
    height: 6px;
    border-radius: 6px;
    background-color: rgba(25, 25, 25);
    overflow: hidden;
  `,
  sweep: css`
    position: relative;
    width: 120px;
    height: 6px;
    background: linear-gradient(
      90deg,
      rgba(245, 95, 62, 0) 0%,
      rgb(245, 95, 62) 28%,
      rgb(255, 136, 51) 50%,
      rgb(255, 194, 61) 72%,
      rgba(255, 194, 61, 0) 100%
    );

    animation: sweep ease-in 1.5s infinite;

    @keyframes sweep {
      from {
        left: -300px;
      }

      to {
        left: 300px;
      }
    }
  `,
  grot: css`
    position: absolute;
    height: 90px;
    top: 190px;

    animation:
      move linear 2.5s forwards,
      hop 2.5s forwards;

    @keyframes move {
      0% {
        transform: translateX(-380px);
        opacity: 0;
        animation-timing-function: ease-in;
      }

      10% {
        transform: translateX(-350px);
        opacity: 1;
        animation-timing-function: ease-out;
      }

      68% {
        transform: translateX(-130px);
        animation-timing-function: ease-in;
      }

      84% {
        transform: translateX(-90px);
        animation-timing-function: linear;
        scale: 1;
        opacity: 1;
      }

      100% {
        transform: translateX(-20px);
        animation-timing-function: ease-out;
        scale: 0.7;
        opacity: 0;
      }
    }

    @keyframes hop {
      0% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      5% {
        top: 170px;
        animation-timing-function: ease-in;
      }

      10% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      15% {
        top: 170px;
        animation-timing-function: ease-in;
      }

      20% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      25% {
        top: 170px;
        animation-timing-function: ease-in;
      }

      30% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      35% {
        top: 170px;
        animation-timing-function: ease-in;
      }

      40% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      44% {
        top: 175px;
        animation-timing-function: ease-in;
      }

      48% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      68% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      76% {
        top: 160px;
        animation-timing-function: ease-in;
      }

      84% {
        top: 180px;
        animation-timing-function: ease-out;
      }

      92% {
        top: 130px;
        animation-timing-function: ease-in;
      }

      100% {
        top: 160px;
        animation-timing-function: ease-out;
      }
    }
  `,
  rocket: css`
    width: 52px;
  `,
  rocketContainer: css`
    position: absolute;
    top: 170px;

    animation:
      stage1-rotate 0.5s 2.4s ease-in-out forwards,
      stage2-move 1.5s 2.9s cubic-bezier(0.21, -0.02, 0.66, 1) forwards,
      init-stage3-float 1.5s 4.4s ease-in-out,
      stage3-float 3s 5.9s ease-in-out infinite alternate,
      init-stage3-drift 3s 4.4s ease-in-out,
      stage3-drift 3s 7.4s ease-in-out infinite alternate;

    animation-composition: accumulate;

    @keyframes stage1-rotate {
      0% {
        transform: translateX(0) rotate(0deg);
      }

      100% {
        transform: translateX(0) rotate(90deg);
      }
    }

    @keyframes stage2-move {
      0% {
        transform: translateX(0);
      }

      100% {
        transform: translateX(250px);
      }
    }

    @keyframes init-stage3-float {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(-5deg);
      }
    }

    @keyframes init-stage3-drift {
      from {
      }

      to {
        transform: translateY(-10px);
      }
    }

    @keyframes stage3-float {
      from {
        transform: rotate(-5deg);
      }

      to {
        transform: rotate(5deg);
      }
    }

    @keyframes stage3-drift {
      from {
        transform: translateY(-10px);
      }

      to {
        transform: translateY(10px);
      }
    }
  `,
  rocketTrail: css`
    position: absolute;
    top: 103px;
    left: 26px;
    width: 70px;
    height: 11px;
    background: linear-gradient(
      90deg,
      rgba(255, 194, 61, 0.9) 0%,
      rgba(255, 136, 51, 0.55) 30%,
      rgba(245, 95, 62, 0) 100%
    );
    transform-origin: 0% 50%;
    transform: scaleY(0%) rotate(90deg);
    filter: blur(1px);

    animation: trail ease-in-out 2s 2.9s;

    @keyframes trail {
      0% {
        transform: scaleY(0%) rotate(90deg);
      }

      10% {
        transform: scaleY(100%) rotate(90deg);
      }

      70% {
        transform: scaleY(100%) rotate(90deg);
        opacity: 1;
      }

      100% {
        transform: scaleY(100%) rotate(90deg);
        opacity: 0;
      }
    }
  `,
});

export default GrotRocketAnimationLoader;
